import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Users,
  Building2,
  Bell,
  FileText,
  Settings,
  Link2,
  Plus,
  Loader2,
  Shield,
  Edit,
  Trash2,
  MoreHorizontal,
  Layers,
  Key,
} from 'lucide-react';
import { toast } from 'sonner';

const roleConfig = {
  super_admin: { label: 'Super Admin', class: 'bg-purple-100 text-purple-700' },
  org_admin: { label: 'Org Admin', class: 'bg-blue-100 text-blue-700' },
  dept_admin: { label: 'Dept Admin', class: 'bg-cyan-100 text-cyan-700' },
  clinician: { label: 'Clinician', class: 'bg-green-100 text-green-700' },
  technician: { label: 'Technician', class: 'bg-slate-100 text-slate-700' },
};

const statusConfig = {
  active: { label: 'Active', class: 'bg-green-100 text-green-700' },
  inactive: { label: 'Inactive', class: 'bg-slate-100 text-slate-700' },
  suspended: { label: 'Suspended', class: 'bg-red-100 text-red-700' },
};

export const AdminConsolePage = () => {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [alertRules, setAlertRules] = useState([]);
  const [activeTab, setActiveTab] = useState('users');
  
  // User dialogs
  const [isCreateUserOpen, setIsCreateUserOpen] = useState(false);
  const [isEditUserOpen, setIsEditUserOpen] = useState(false);
  const [isDeleteUserOpen, setIsDeleteUserOpen] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Department dialogs
  const [isCreateDeptOpen, setIsCreateDeptOpen] = useState(false);
  const [isEditDeptOpen, setIsEditDeptOpen] = useState(false);
  const [selectedDept, setSelectedDept] = useState(null);
  
  // Organization dialogs
  const [isCreateOrgOpen, setIsCreateOrgOpen] = useState(false);
  const [isEditOrgOpen, setIsEditOrgOpen] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  
  // Alert Rule dialogs
  const [isCreateRuleOpen, setIsCreateRuleOpen] = useState(false);
  const [isEditRuleOpen, setIsEditRuleOpen] = useState(false);
  const [isDeleteRuleOpen, setIsDeleteRuleOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  
  // Form states
  const [userForm, setUserForm] = useState({
    full_name: '',
    email: '',
    username: '',
    password: '',
    phone: '',
    role: 'clinician',
    dept_id: '',
    org_id: '',
    specialization: '',
    status: 'active',
  });

  const [orgForm, setOrgForm] = useState({
    org_name: '',
    org_code: '',
    address: '',
    city: '',
    state: '',
    country: '',
    postal_code: '',
    contact_email: '',
    contact_phone: '',
    website: '',
    status: 'active',
  });

  const [deptForm, setDeptForm] = useState({
    dept_name: '',
    dept_code: '',
    dept_type: 'general',
    org_id: '',
    head_of_dept_name: '',
    address: '',
    city: '',
    state: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
  });

  const [ruleForm, setRuleForm] = useState({
    rule_name: '',
    description: '',
    rule_type: 'threshold',
    is_active: true,
    critical_threshold: 0.95,
    high_threshold: 0.85,
    medium_threshold: 0.70,
    auto_escalate_critical_minutes: 30,
    auto_escalate_high_minutes: 120,
    auto_escalate_medium_minutes: 480,
    notify_on_critical: true,
    notify_on_high: true,
    notify_on_medium: false,
    notify_on_low: false,
    notification_channels: ['in_app'],
    escalation_chain: ['clinician', 'dept_admin', 'org_admin'],
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, deptsRes, orgsRes, rulesRes] = await Promise.all([
        api.get('/users'),
        api.get('/departments'),
        api.get('/organizations'),
        api.get('/config/alert-rules'),
      ]);
      setUsers(usersRes.data?.users || usersRes.data || []);
      setDepartments(deptsRes.data?.departments || deptsRes.data || []);
      setOrganizations(orgsRes.data?.organizations || orgsRes.data || []);
      setAlertRules(rulesRes.data?.alert_rules || rulesRes.data || []);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const resetUserForm = () => {
    setUserForm({
      full_name: '',
      email: '',
      username: '',
      password: '',
      phone: '',
      role: 'clinician',
      dept_id: '',
      specialization: '',
      status: 'active',
    });
  };

  const resetOrgForm = () => {
    setOrgForm({
      org_name: '',
      org_code: '',
      address: '',
      city: '',
      state: '',
      country: '',
      postal_code: '',
      contact_email: '',
      contact_phone: '',
      website: '',
      status: 'active',
    });
  };

  const resetDeptForm = () => {
    setDeptForm({
      dept_name: '',
      dept_code: '',
      dept_type: 'general',
      org_id: '',
      head_of_dept_name: '',
      address: '',
      city: '',
      state: '',
      contact_name: '',
      contact_email: '',
      contact_phone: '',
    });
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!userForm.org_id) {
      toast.error('Please select an organization');
      return;
    }
    try {
      await api.post('/users', {
        email: userForm.email,
        username: userForm.username || null,
        full_name: userForm.full_name,
        password: userForm.password,
        phone: userForm.phone || null,
        role: userForm.role,
        org_id: userForm.org_id,
        dept_id: userForm.dept_id || null,
        specialization: userForm.specialization || null,
        status: userForm.status,
      });
      toast.success('User created successfully');
      setIsCreateUserOpen(false);
      resetUserForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    
    try {
      await api.patch(`/users/${selectedUser.user_id}`, {
        full_name: userForm.full_name,
        username: userForm.username || null,
        phone: userForm.phone || null,
        role: userForm.role,
        org_id: userForm.org_id || null,
        dept_id: userForm.dept_id || null,
        specialization: userForm.specialization || null,
        status: userForm.status,
      });
      toast.success('User updated successfully');
      setIsEditUserOpen(false);
      setSelectedUser(null);
      resetUserForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    
    try {
      await api.delete(`/users/${selectedUser.user_id}`);
      toast.success('User deactivated');
      setIsDeleteUserOpen(false);
      setSelectedUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const openEditUser = (u) => {
    setSelectedUser(u);
    setUserForm({
      full_name: u.full_name || '',
      email: u.email || '',
      username: u.username || '',
      password: '',
      phone: u.phone || '',
      role: u.role || 'clinician',
      org_id: u.org_id || '',
      dept_id: u.dept_id || '',
      specialization: u.specialization || '',
      status: u.status || 'active',
    });
    setIsEditUserOpen(true);
  };

  const openDeleteUser = (u) => {
    setSelectedUser(u);
    setIsDeleteUserOpen(true);
  };

  const openChangePassword = (u) => {
    setSelectedUser(u);
    setNewPassword('');
    setIsChangePasswordOpen(true);
  };

  const handleChangePassword = async () => {
    if (!selectedUser || !newPassword) return;
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('new_password', newPassword);
      
      await api.post(`/users/${selectedUser.user_id}/change-password`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Password changed successfully');
      setIsChangePasswordOpen(false);
      setSelectedUser(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
  };

  const handleCreateOrg = async (e) => {
    e.preventDefault();
    try {
      await api.post('/organizations', {
        org_name: orgForm.org_name,
        org_code: orgForm.org_code?.toUpperCase(),
        address: orgForm.address || null,
        city: orgForm.city || null,
        state: orgForm.state || null,
        country: orgForm.country || null,
        postal_code: orgForm.postal_code || null,
        contact_email: orgForm.contact_email || null,
        contact_phone: orgForm.contact_phone || null,
        website: orgForm.website || null,
      });
      toast.success('Organization created successfully');
      setIsCreateOrgOpen(false);
      resetOrgForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create organization');
    }
  };

  const handleEditOrg = async (e) => {
    e.preventDefault();
    if (!selectedOrg) return;
    
    try {
      await api.patch(`/organizations/${selectedOrg.org_id}`, {
        org_name: orgForm.org_name,
        address: orgForm.address || null,
        city: orgForm.city || null,
        state: orgForm.state || null,
        country: orgForm.country || null,
        postal_code: orgForm.postal_code || null,
        contact_email: orgForm.contact_email || null,
        contact_phone: orgForm.contact_phone || null,
        website: orgForm.website || null,
        status: orgForm.status,
      });
      toast.success('Organization updated successfully');
      setIsEditOrgOpen(false);
      setSelectedOrg(null);
      resetOrgForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update organization');
    }
  };

  const openEditOrg = (org) => {
    setSelectedOrg(org);
    setOrgForm({
      org_name: org.org_name || '',
      org_code: org.org_code || '',
      address: org.address || '',
      city: org.city || '',
      state: org.state || '',
      country: org.country || '',
      postal_code: org.postal_code || '',
      contact_email: org.contact_email || '',
      contact_phone: org.contact_phone || '',
      website: org.website || '',
      status: org.status || 'active',
    });
    setIsEditOrgOpen(true);
  };

  const handleCreateDept = async (e) => {
    e.preventDefault();
    try {
      await api.post('/departments', {
        dept_name: deptForm.dept_name,
        dept_code: deptForm.dept_code?.toUpperCase(),
        dept_type: deptForm.dept_type || 'general',
        org_id: deptForm.org_id || user.org_id,
        head_of_dept_name: deptForm.head_of_dept_name || null,
        address: deptForm.address || null,
        city: deptForm.city || null,
        state: deptForm.state || null,
        contact_name: deptForm.contact_name || null,
        contact_email: deptForm.contact_email || null,
        contact_phone: deptForm.contact_phone || null,
      });
      toast.success('Department created successfully');
      setIsCreateDeptOpen(false);
      resetDeptForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create department');
    }
  };

  const getDeptName = (deptId) => {
    const dept = departments.find(d => d.dept_id === deptId);
    return dept?.dept_name || '-';
  };

  const getOrgName = (orgId) => {
    const org = organizations.find(o => o.org_id === orgId);
    return org?.org_name || '-';
  };

  // Alert Rule functions
  const resetRuleForm = () => {
    setRuleForm({
      rule_name: '',
      description: '',
      rule_type: 'threshold',
      is_active: true,
      critical_threshold: 0.95,
      high_threshold: 0.85,
      medium_threshold: 0.70,
      auto_escalate_critical_minutes: 30,
      auto_escalate_high_minutes: 120,
      auto_escalate_medium_minutes: 480,
      notify_on_critical: true,
      notify_on_high: true,
      notify_on_medium: false,
      notify_on_low: false,
      notification_channels: ['in_app'],
      escalation_chain: ['clinician', 'dept_admin', 'org_admin'],
    });
  };

  const handleCreateRule = async (e) => {
    e.preventDefault();
    try {
      await api.post('/config/alert-rules', ruleForm);
      toast.success('Alert rule created successfully');
      setIsCreateRuleOpen(false);
      resetRuleForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create alert rule');
    }
  };

  const handleEditRule = async (e) => {
    e.preventDefault();
    if (!selectedRule) return;
    try {
      await api.patch(`/config/alert-rules/${selectedRule.rule_id}`, ruleForm);
      toast.success('Alert rule updated successfully');
      setIsEditRuleOpen(false);
      setSelectedRule(null);
      resetRuleForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update alert rule');
    }
  };

  const handleDeleteRule = async () => {
    if (!selectedRule) return;
    try {
      await api.delete(`/config/alert-rules/${selectedRule.rule_id}`);
      toast.success('Alert rule deleted successfully');
      setIsDeleteRuleOpen(false);
      setSelectedRule(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete alert rule');
    }
  };

  const openEditRule = (rule) => {
    setSelectedRule(rule);
    setRuleForm({
      rule_name: rule.rule_name || '',
      description: rule.description || '',
      rule_type: rule.rule_type || 'threshold',
      is_active: rule.is_active !== false,
      critical_threshold: rule.critical_threshold ?? 0.95,
      high_threshold: rule.high_threshold ?? 0.85,
      medium_threshold: rule.medium_threshold ?? 0.70,
      auto_escalate_critical_minutes: rule.auto_escalate_critical_minutes ?? 30,
      auto_escalate_high_minutes: rule.auto_escalate_high_minutes ?? 120,
      auto_escalate_medium_minutes: rule.auto_escalate_medium_minutes ?? 480,
      notify_on_critical: rule.notify_on_critical !== false,
      notify_on_high: rule.notify_on_high !== false,
      notify_on_medium: rule.notify_on_medium === true,
      notify_on_low: rule.notify_on_low === true,
      notification_channels: rule.notification_channels || ['in_app'],
      escalation_chain: rule.escalation_chain || ['clinician', 'dept_admin', 'org_admin'],
    });
    setIsEditRuleOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="admin-console-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Admin Console</h1>
          <p className="text-muted-foreground mt-1">
            Manage users, departments, and system configuration
          </p>
        </div>
      </div>

      {/* Scope Banner */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <Shield className="w-5 h-5 text-blue-600" />
            <div>
              <p className="font-medium text-blue-800">Organization Scope</p>
              <p className="text-sm text-blue-700">
                Managing settings for your organization. Users and departments are shared across meragoScribe and meragoAnalyze.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5 lg:w-auto lg:inline-flex">
          <TabsTrigger value="organizations" className="gap-2" data-testid="organizations-tab">
            <Building2 className="w-4 h-4" />
            <span className="hidden sm:inline">Organizations</span>
          </TabsTrigger>
          <TabsTrigger value="users" className="gap-2" data-testid="users-tab">
            <Users className="w-4 h-4" />
            <span className="hidden sm:inline">Users</span>
          </TabsTrigger>
          <TabsTrigger value="departments" className="gap-2" data-testid="departments-tab">
            <Layers className="w-4 h-4" />
            <span className="hidden sm:inline">Departments</span>
          </TabsTrigger>
          <TabsTrigger value="alerts" className="gap-2" data-testid="alerts-tab">
            <Bell className="w-4 h-4" />
            <span className="hidden sm:inline">Alert Rules</span>
          </TabsTrigger>
          <TabsTrigger value="connectors" className="gap-2" data-testid="connectors-tab">
            <Link2 className="w-4 h-4" />
            <span className="hidden sm:inline">Connectors</span>
          </TabsTrigger>
        </TabsList>

        {/* Organizations Tab */}
        <TabsContent value="organizations" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Organizations ({organizations.length})</CardTitle>
                {user?.role === 'super_admin' && (
                  <Dialog open={isCreateOrgOpen} onOpenChange={(open) => { setIsCreateOrgOpen(open); if (open) resetOrgForm(); }}>
                    <DialogTrigger asChild>
                      <Button data-testid="create-org-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Organization
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-lg">
                      <DialogHeader>
                        <DialogTitle>Create New Organization</DialogTitle>
                        <DialogDescription>
                          Add a new tenant organization
                        </DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleCreateOrg} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="org_name">Organization Name *</Label>
                            <Input 
                              id="org_name" 
                              value={orgForm.org_name}
                              onChange={(e) => setOrgForm({...orgForm, org_name: e.target.value})}
                              placeholder="Merago Healthcare" 
                              required 
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="org_code">Organization Code *</Label>
                            <Input 
                              id="org_code" 
                              value={orgForm.org_code}
                              onChange={(e) => setOrgForm({...orgForm, org_code: e.target.value.toUpperCase()})}
                              placeholder="MERAGO"
                              pattern="[A-Za-z0-9_-]{2,10}"
                              className="uppercase"
                              required 
                            />
                            <p className="text-xs text-muted-foreground">2-10 alphanumeric</p>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="address">Address</Label>
                          <Input 
                            id="address" 
                            value={orgForm.address}
                            onChange={(e) => setOrgForm({...orgForm, address: e.target.value})}
                            placeholder="123 Medical Center Drive" 
                          />
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="city">City</Label>
                            <Input 
                              id="city" 
                              value={orgForm.city}
                              onChange={(e) => setOrgForm({...orgForm, city: e.target.value})}
                              placeholder="New York" 
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="state">State</Label>
                            <Input 
                              id="state" 
                              value={orgForm.state}
                              onChange={(e) => setOrgForm({...orgForm, state: e.target.value})}
                              placeholder="NY" 
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="country">Country</Label>
                            <Input 
                              id="country" 
                              value={orgForm.country}
                              onChange={(e) => setOrgForm({...orgForm, country: e.target.value})}
                              placeholder="USA" 
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="contact_email">Contact Email</Label>
                            <Input 
                              id="contact_email" 
                              type="email"
                              value={orgForm.contact_email}
                              onChange={(e) => setOrgForm({...orgForm, contact_email: e.target.value})}
                              placeholder="admin@org.com" 
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="contact_phone">Contact Phone</Label>
                            <Input 
                              id="contact_phone" 
                              value={orgForm.contact_phone}
                              onChange={(e) => setOrgForm({...orgForm, contact_phone: e.target.value})}
                              placeholder="+1 (555) 123-4567" 
                            />
                          </div>
                        </div>
                        <DialogFooter>
                          <Button type="button" variant="outline" onClick={() => setIsCreateOrgOpen(false)}>
                            Cancel
                          </Button>
                          <Button type="submit">Create Organization</Button>
                        </DialogFooter>
                      </form>
                    </DialogContent>
                  </Dialog>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Organization</TableHead>
                      <TableHead>Code</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-12">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {organizations.map((org) => (
                      <TableRow key={org.org_id} data-testid={`org-row-${org.org_id}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                              <Building2 className="w-5 h-5 text-blue-500" />
                            </div>
                            <div>
                              <p className="font-medium">{org.org_name}</p>
                              <p className="text-xs text-muted-foreground">{org.org_id?.slice(0, 8)}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="font-mono">{org.org_code || '-'}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {org.city && org.state ? `${org.city}, ${org.state}` : org.city || org.state || '-'}
                            {org.country && <p className="text-xs text-muted-foreground">{org.country}</p>}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {org.contact_email && <p className="text-slate-600">{org.contact_email}</p>}
                            {org.contact_phone && <p className="text-xs text-muted-foreground">{org.contact_phone}</p>}
                            {!org.contact_email && !org.contact_phone && <span className="text-muted-foreground">-</span>}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={org.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}>
                            {org.status === 'active' ? 'Active' : org.status || 'Active'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEditOrg(org)}>
                                <Edit className="w-4 h-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Users ({users.length})</CardTitle>
                <Dialog open={isCreateUserOpen} onOpenChange={setIsCreateUserOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid="create-user-btn" onClick={resetUserForm}>
                      <Plus className="w-4 h-4 mr-2" />
                      Add User
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                      <DialogTitle>Create New User</DialogTitle>
                      <DialogDescription>
                        Add a new user to your organization
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateUser} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="full_name">Full Name *</Label>
                          <Input 
                            id="full_name" 
                            value={userForm.full_name}
                            onChange={(e) => setUserForm({...userForm, full_name: e.target.value})}
                            required 
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email">Email *</Label>
                          <Input 
                            id="email" 
                            type="email" 
                            value={userForm.email}
                            onChange={(e) => setUserForm({...userForm, email: e.target.value})}
                            required 
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="username">Username</Label>
                          <Input 
                            id="username" 
                            value={userForm.username}
                            onChange={(e) => setUserForm({...userForm, username: e.target.value})}
                            placeholder="Optional - for login"
                            data-testid="user-username-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="password">Password *</Label>
                          <Input 
                            id="password" 
                            type="password" 
                            value={userForm.password}
                            onChange={(e) => setUserForm({...userForm, password: e.target.value})}
                            required 
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="phone">Phone</Label>
                          <Input 
                            id="phone" 
                            value={userForm.phone}
                            onChange={(e) => setUserForm({...userForm, phone: e.target.value})}
                            placeholder="+1 (555) 123-4567"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="role">Role *</Label>
                          <Select value={userForm.role} onValueChange={(v) => setUserForm({...userForm, role: v})}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {user?.role === 'super_admin' && (
                                <SelectItem value="sys_admin">System Admin</SelectItem>
                              )}
                              <SelectItem value="org_admin">Org Admin</SelectItem>
                              <SelectItem value="dept_admin">Dept Admin</SelectItem>
                              <SelectItem value="clinician">Clinician (Doctor)</SelectItem>
                              <SelectItem value="technician">Technician</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="org_id">Organization *</Label>
                          <Select value={userForm.org_id} onValueChange={(v) => {
                            setUserForm({...userForm, org_id: v, dept_id: ''});
                          }}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select organization" />
                            </SelectTrigger>
                            <SelectContent>
                              {organizations.map((org) => (
                                <SelectItem key={org.org_id} value={org.org_id}>
                                  {org.org_name} ({org.org_code})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="dept_id">Department</Label>
                        <Select 
                          value={userForm.dept_id} 
                          onValueChange={(v) => setUserForm({...userForm, dept_id: v})}
                          disabled={!userForm.org_id}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={userForm.org_id ? "Select department" : "Select organization first"} />
                          </SelectTrigger>
                          <SelectContent>
                            {departments
                              .filter(dept => dept.org_id === userForm.org_id)
                              .map((dept) => (
                                <SelectItem key={dept.dept_id} value={dept.dept_id}>
                                  {dept.dept_name}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {(userForm.role === 'clinician' || userForm.role === 'org_admin') && (
                        <div className="space-y-2">
                          <Label htmlFor="specialization">Specialization</Label>
                          <Select value={userForm.specialization} onValueChange={(v) => setUserForm({...userForm, specialization: v})}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select specialization" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="general_medicine">General Medicine</SelectItem>
                              <SelectItem value="pathology">Pathology</SelectItem>
                              <SelectItem value="radiology">Radiology</SelectItem>
                              <SelectItem value="cardiology">Cardiology</SelectItem>
                              <SelectItem value="neurology">Neurology</SelectItem>
                              <SelectItem value="oncology">Oncology</SelectItem>
                              <SelectItem value="internal_medicine">Internal Medicine</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setIsCreateUserOpen(false)}>
                          Cancel
                        </Button>
                        <Button type="submit">Create User</Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Organization</TableHead>
                      <TableHead>Department</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-12">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((u) => (
                      <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{u.full_name}</p>
                            {u.specialization && (
                              <p className="text-xs text-muted-foreground capitalize">{u.specialization?.replace('_', ' ')}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell>{u.phone || '-'}</TableCell>
                        <TableCell>
                          <span className="text-sm">{getOrgName(u.org_id)}</span>
                        </TableCell>
                        <TableCell>{getDeptName(u.dept_id)}</TableCell>
                        <TableCell>
                          <Badge className={roleConfig[u.role]?.class}>
                            {roleConfig[u.role]?.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={statusConfig[u.status]?.class}>
                            {statusConfig[u.status]?.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEditUser(u)}>
                                <Edit className="w-4 h-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => openChangePassword(u)}
                                disabled={u.user_id === user.user_id}
                              >
                                <Key className="w-4 h-4 mr-2" />
                                Change Password
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => openDeleteUser(u)}
                                className="text-red-600"
                                disabled={u.user_id === user.user_id}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Departments Tab */}
        <TabsContent value="departments" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Departments ({departments.length})</CardTitle>
                <Dialog open={isCreateDeptOpen} onOpenChange={(open) => { setIsCreateDeptOpen(open); if (open) resetDeptForm(); }}>
                  <DialogTrigger asChild>
                    <Button data-testid="create-dept-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Department
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                      <DialogTitle>Create New Department</DialogTitle>
                      <DialogDescription>
                        Add a department to an organization
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateDept} className="space-y-4">
                      {/* Organization Selection for Super Admin */}
                      {user?.role === 'super_admin' && (
                        <div className="space-y-2">
                          <Label htmlFor="dept_org_id">Organization *</Label>
                          <Select 
                            value={deptForm.org_id} 
                            onValueChange={(v) => setDeptForm({...deptForm, org_id: v})}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select organization" />
                            </SelectTrigger>
                            <SelectContent>
                              {organizations.map((org) => (
                                <SelectItem key={org.org_id} value={org.org_id}>
                                  {org.org_name} ({org.org_code})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="dept_name">Department Name *</Label>
                          <Input 
                            id="dept_name" 
                            value={deptForm.dept_name}
                            onChange={(e) => setDeptForm({...deptForm, dept_name: e.target.value})}
                            placeholder="Pathology Lab" 
                            required 
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="dept_code">Department Code *</Label>
                          <Input 
                            id="dept_code" 
                            value={deptForm.dept_code}
                            onChange={(e) => setDeptForm({...deptForm, dept_code: e.target.value.toUpperCase()})}
                            placeholder="PATH01"
                            pattern="[A-Za-z0-9_-]{2,10}"
                            className="uppercase"
                            required 
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="dept_type">Department Type *</Label>
                        <Select 
                          value={deptForm.dept_type} 
                          onValueChange={(v) => setDeptForm({...deptForm, dept_type: v})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pathology">Pathology</SelectItem>
                            <SelectItem value="radiology">Radiology</SelectItem>
                            <SelectItem value="cardiology">Cardiology</SelectItem>
                            <SelectItem value="neurology">Neurology</SelectItem>
                            <SelectItem value="general">General</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="head_of_dept_name">Head of Department</Label>
                        <Input 
                          id="head_of_dept_name" 
                          value={deptForm.head_of_dept_name}
                          onChange={(e) => setDeptForm({...deptForm, head_of_dept_name: e.target.value})}
                          placeholder="Dr. John Smith" 
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="dept_address">Address</Label>
                          <Input 
                            id="dept_address" 
                            value={deptForm.address}
                            onChange={(e) => setDeptForm({...deptForm, address: e.target.value})}
                            placeholder="Building A, Floor 2" 
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="dept_city">City</Label>
                          <Input 
                            id="dept_city" 
                            value={deptForm.city}
                            onChange={(e) => setDeptForm({...deptForm, city: e.target.value})}
                            placeholder="New York" 
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="contact_name">Contact Name</Label>
                        <Input 
                          id="contact_name" 
                          value={deptForm.contact_name}
                          onChange={(e) => setDeptForm({...deptForm, contact_name: e.target.value})}
                          placeholder="Jane Doe" 
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="dept_contact_email">Contact Email</Label>
                          <Input 
                            id="dept_contact_email" 
                            type="email"
                            value={deptForm.contact_email}
                            onChange={(e) => setDeptForm({...deptForm, contact_email: e.target.value})}
                            placeholder="dept@hospital.com" 
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="dept_contact_phone">Contact Phone</Label>
                          <Input 
                            id="dept_contact_phone" 
                            value={deptForm.contact_phone}
                            onChange={(e) => setDeptForm({...deptForm, contact_phone: e.target.value})}
                            placeholder="+1 (555) 123-4567" 
                          />
                        </div>
                      </div>
                      <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setIsCreateDeptOpen(false)}>
                          Cancel
                        </Button>
                        <Button type="submit" disabled={user?.role === 'super_admin' && !deptForm.org_id}>
                          Create Department
                        </Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Department</TableHead>
                      <TableHead>Code</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Head of Dept</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {departments.map((dept) => (
                      <TableRow key={dept.dept_id} data-testid={`dept-row-${dept.dept_id}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                              <Layers className="w-5 h-5 text-indigo-500" />
                            </div>
                            <div>
                              <p className="font-medium">{dept.dept_name}</p>
                              {dept.address && <p className="text-xs text-muted-foreground">{dept.address}</p>}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="font-mono">{dept.dept_code || '-'}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="capitalize">{dept.dept_type || 'general'}</Badge>
                        </TableCell>
                        <TableCell>{dept.head_of_dept_name || '-'}</TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {dept.contact_email && <p className="text-slate-600">{dept.contact_email}</p>}
                            {dept.contact_phone && <p className="text-xs text-muted-foreground">{dept.contact_phone}</p>}
                            {!dept.contact_email && !dept.contact_phone && <span className="text-muted-foreground">-</span>}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={dept.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}>
                            {dept.status === 'active' ? 'Active' : dept.status || 'Active'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Alert Rules Tab */}
        <TabsContent value="alerts" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Alert Rules Configuration</CardTitle>
              <Button onClick={() => { resetRuleForm(); setIsCreateRuleOpen(true); }} data-testid="create-alert-rule-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Rule
              </Button>
            </CardHeader>
            <CardContent>
              {alertRules.length === 0 ? (
                <div className="text-center py-12">
                  <Bell className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                  <p className="text-muted-foreground">No alert rules configured</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Create your first rule to configure thresholds and escalations
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {alertRules.map((rule) => (
                    <div key={rule.rule_id} className="border rounded-lg p-4" data-testid={`alert-rule-${rule.rule_id}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-slate-900">{rule.rule_name}</h3>
                            <Badge variant={rule.is_active ? 'default' : 'secondary'}>
                              {rule.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </div>
                          {rule.description && (
                            <p className="text-sm text-muted-foreground mt-1">{rule.description}</p>
                          )}
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditRule(rule)}>
                              <Edit className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => { setSelectedRule(rule); setIsDeleteRuleOpen(true); }}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                        {/* Thresholds */}
                        <div className="bg-slate-50 rounded-lg p-3">
                          <h4 className="text-xs font-medium text-slate-500 uppercase mb-2">Confidence Thresholds</h4>
                          <div className="space-y-1 text-sm">
                            <div className="flex justify-between">
                              <span className="text-red-600">Critical:</span>
                              <span className="font-medium">{((rule.critical_threshold || 0.95) * 100).toFixed(0)}%</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-orange-600">High:</span>
                              <span className="font-medium">{((rule.high_threshold || 0.85) * 100).toFixed(0)}%</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-yellow-600">Medium:</span>
                              <span className="font-medium">{((rule.medium_threshold || 0.70) * 100).toFixed(0)}%</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Escalation */}
                        <div className="bg-slate-50 rounded-lg p-3">
                          <h4 className="text-xs font-medium text-slate-500 uppercase mb-2">Auto-Escalation</h4>
                          <div className="space-y-1 text-sm">
                            <div className="flex justify-between">
                              <span>Critical:</span>
                              <span className="font-medium">{rule.auto_escalate_critical_minutes || 30} min</span>
                            </div>
                            <div className="flex justify-between">
                              <span>High:</span>
                              <span className="font-medium">{rule.auto_escalate_high_minutes || 120} min</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Medium:</span>
                              <span className="font-medium">{rule.auto_escalate_medium_minutes || 480} min</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Notifications */}
                        <div className="bg-slate-50 rounded-lg p-3">
                          <h4 className="text-xs font-medium text-slate-500 uppercase mb-2">Notifications</h4>
                          <div className="flex flex-wrap gap-1">
                            {rule.notify_on_critical && <Badge variant="outline" className="text-red-600 border-red-200">Critical</Badge>}
                            {rule.notify_on_high && <Badge variant="outline" className="text-orange-600 border-orange-200">High</Badge>}
                            {rule.notify_on_medium && <Badge variant="outline" className="text-yellow-600 border-yellow-200">Medium</Badge>}
                            {rule.notify_on_low && <Badge variant="outline" className="text-slate-600 border-slate-200">Low</Badge>}
                          </div>
                          <div className="mt-2 text-xs text-muted-foreground">
                            Channels: {(rule.notification_channels || ['in_app']).join(', ')}
                          </div>
                        </div>
                      </div>
                      
                      {/* Escalation Chain */}
                      <div className="mt-3 pt-3 border-t">
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-muted-foreground">Escalation chain:</span>
                          <div className="flex items-center gap-1">
                            {(rule.escalation_chain || ['clinician', 'dept_admin', 'org_admin']).map((role, idx) => (
                              <span key={role}>
                                <Badge variant="secondary" className="capitalize text-xs">
                                  {role.replace('_', ' ')}
                                </Badge>
                                {idx < (rule.escalation_chain || []).length - 1 && <span className="text-muted-foreground mx-1">→</span>}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Connectors Tab */}
        <TabsContent value="connectors" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">External Connectors</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12">
                <Link2 className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                <p className="text-muted-foreground">No external connectors configured</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Lab systems, EMR integrations, and HealthVault connections will appear here
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit User Dialog */}
      <Dialog open={isEditUserOpen} onOpenChange={setIsEditUserOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              Update user details
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditUser} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_full_name">Full Name *</Label>
                <Input 
                  id="edit_full_name" 
                  value={userForm.full_name}
                  onChange={(e) => setUserForm({...userForm, full_name: e.target.value})}
                  required 
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_email">Email</Label>
                <Input 
                  id="edit_email" 
                  type="email" 
                  value={userForm.email}
                  disabled
                  className="bg-slate-100"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_username">Username</Label>
                <Input 
                  id="edit_username" 
                  value={userForm.username}
                  onChange={(e) => setUserForm({...userForm, username: e.target.value})}
                  placeholder="Optional - for login"
                  data-testid="edit-user-username-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_phone">Phone</Label>
                <Input 
                  id="edit_phone" 
                  value={userForm.phone}
                  onChange={(e) => setUserForm({...userForm, phone: e.target.value})}
                  placeholder="+1 (555) 123-4567"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_status">Status</Label>
                <Select value={userForm.status} onValueChange={(v) => setUserForm({...userForm, status: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_role">Role</Label>
                <Select value={userForm.role} onValueChange={(v) => setUserForm({...userForm, role: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {user?.role === 'super_admin' && (
                      <SelectItem value="sys_admin">System Admin</SelectItem>
                    )}
                    <SelectItem value="org_admin">Org Admin</SelectItem>
                    <SelectItem value="dept_admin">Dept Admin</SelectItem>
                    <SelectItem value="clinician">Clinician (Doctor)</SelectItem>
                    <SelectItem value="technician">Technician</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_org_id">Organization</Label>
                <Select value={userForm.org_id} onValueChange={(v) => {
                  setUserForm({...userForm, org_id: v, dept_id: ''});
                }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select organization" />
                  </SelectTrigger>
                  <SelectContent>
                    {organizations.map((org) => (
                      <SelectItem key={org.org_id} value={org.org_id}>
                        {org.org_name} ({org.org_code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit_dept_id">Department</Label>
              <Select 
                value={userForm.dept_id} 
                onValueChange={(v) => setUserForm({...userForm, dept_id: v})}
                disabled={!userForm.org_id}
              >
                <SelectTrigger>
                  <SelectValue placeholder={userForm.org_id ? "Select department" : "Select organization first"} />
                </SelectTrigger>
                <SelectContent>
                  {departments
                    .filter(dept => dept.org_id === userForm.org_id)
                    .map((dept) => (
                      <SelectItem key={dept.dept_id} value={dept.dept_id}>
                        {dept.dept_name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            {(userForm.role === 'clinician' || userForm.role === 'org_admin') && (
              <div className="space-y-2">
                <Label htmlFor="edit_specialization">Specialization</Label>
                <Select value={userForm.specialization} onValueChange={(v) => setUserForm({...userForm, specialization: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select specialization" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general_medicine">General Medicine</SelectItem>
                    <SelectItem value="pathology">Pathology</SelectItem>
                    <SelectItem value="radiology">Radiology</SelectItem>
                    <SelectItem value="cardiology">Cardiology</SelectItem>
                    <SelectItem value="neurology">Neurology</SelectItem>
                    <SelectItem value="oncology">Oncology</SelectItem>
                    <SelectItem value="internal_medicine">Internal Medicine</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsEditUserOpen(false)}>
                Cancel
              </Button>
              <Button type="submit">Save Changes</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation */}
      <AlertDialog open={isDeleteUserOpen} onOpenChange={setIsDeleteUserOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to deactivate {selectedUser?.full_name}? 
              They will no longer be able to access the system.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteUser} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Change Password Dialog */}
      <Dialog open={isChangePasswordOpen} onOpenChange={setIsChangePasswordOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>
              Set a new password for {selectedUser?.full_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new_password">New Password</Label>
              <Input 
                id="new_password" 
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min. 6 characters)"
                data-testid="change-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsChangePasswordOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleChangePassword}
              disabled={!newPassword || newPassword.length < 6}
              data-testid="change-password-submit-btn"
            >
              Change Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Organization Dialog */}
      <Dialog open={isEditOrgOpen} onOpenChange={setIsEditOrgOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Organization</DialogTitle>
            <DialogDescription>
              Update organization details
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditOrg} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_org_name">Organization Name *</Label>
                <Input 
                  id="edit_org_name" 
                  value={orgForm.org_name}
                  onChange={(e) => setOrgForm({...orgForm, org_name: e.target.value})}
                  required 
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_org_code">Organization Code</Label>
                <Input 
                  id="edit_org_code" 
                  value={orgForm.org_code}
                  disabled
                  className="bg-slate-100 uppercase"
                />
                <p className="text-xs text-muted-foreground">Code cannot be changed</p>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit_org_address">Address</Label>
              <Input 
                id="edit_org_address" 
                value={orgForm.address}
                onChange={(e) => setOrgForm({...orgForm, address: e.target.value})}
                placeholder="123 Medical Center Drive" 
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_org_city">City</Label>
                <Input 
                  id="edit_org_city" 
                  value={orgForm.city}
                  onChange={(e) => setOrgForm({...orgForm, city: e.target.value})}
                  placeholder="New York" 
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_org_state">State</Label>
                <Input 
                  id="edit_org_state" 
                  value={orgForm.state}
                  onChange={(e) => setOrgForm({...orgForm, state: e.target.value})}
                  placeholder="NY" 
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_org_country">Country</Label>
                <Input 
                  id="edit_org_country" 
                  value={orgForm.country}
                  onChange={(e) => setOrgForm({...orgForm, country: e.target.value})}
                  placeholder="USA" 
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_org_contact_email">Contact Email</Label>
                <Input 
                  id="edit_org_contact_email" 
                  type="email"
                  value={orgForm.contact_email}
                  onChange={(e) => setOrgForm({...orgForm, contact_email: e.target.value})}
                  placeholder="admin@org.com" 
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_org_contact_phone">Contact Phone</Label>
                <Input 
                  id="edit_org_contact_phone" 
                  value={orgForm.contact_phone}
                  onChange={(e) => setOrgForm({...orgForm, contact_phone: e.target.value})}
                  placeholder="+1 (555) 123-4567" 
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_org_website">Website</Label>
                <Input 
                  id="edit_org_website" 
                  value={orgForm.website}
                  onChange={(e) => setOrgForm({...orgForm, website: e.target.value})}
                  placeholder="https://www.organization.com" 
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_org_status">Status</Label>
                <Select value={orgForm.status} onValueChange={(v) => setOrgForm({...orgForm, status: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsEditOrgOpen(false)}>
                Cancel
              </Button>
              <Button type="submit">Save Changes</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Alert Rule Dialog */}
      <Dialog open={isCreateRuleOpen} onOpenChange={setIsCreateRuleOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Alert Rule</DialogTitle>
            <DialogDescription>
              Configure alert thresholds, escalation rules, and notifications
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateRule} className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="rule_name">Rule Name *</Label>
                <Input 
                  id="rule_name" 
                  value={ruleForm.rule_name}
                  onChange={(e) => setRuleForm({...ruleForm, rule_name: e.target.value})}
                  placeholder="e.g., Critical Lab Values"
                  required 
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="rule_type">Rule Type</Label>
                <Select value={ruleForm.rule_type} onValueChange={(v) => setRuleForm({...ruleForm, rule_type: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="threshold">Threshold Based</SelectItem>
                    <SelectItem value="pattern">Pattern Detection</SelectItem>
                    <SelectItem value="escalation">Escalation Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="rule_description">Description</Label>
              <Input 
                id="rule_description" 
                value={ruleForm.description}
                onChange={(e) => setRuleForm({...ruleForm, description: e.target.value})}
                placeholder="Describe when this rule triggers"
              />
            </div>

            {/* Thresholds */}
            <div className="space-y-3">
              <h4 className="font-medium text-sm">Confidence Thresholds</h4>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-red-600">Critical (%)</Label>
                  <Input 
                    type="number"
                    min="0" max="100" step="1"
                    value={Math.round(ruleForm.critical_threshold * 100)}
                    onChange={(e) => setRuleForm({...ruleForm, critical_threshold: parseInt(e.target.value) / 100})}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-orange-600">High (%)</Label>
                  <Input 
                    type="number"
                    min="0" max="100" step="1"
                    value={Math.round(ruleForm.high_threshold * 100)}
                    onChange={(e) => setRuleForm({...ruleForm, high_threshold: parseInt(e.target.value) / 100})}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-yellow-600">Medium (%)</Label>
                  <Input 
                    type="number"
                    min="0" max="100" step="1"
                    value={Math.round(ruleForm.medium_threshold * 100)}
                    onChange={(e) => setRuleForm({...ruleForm, medium_threshold: parseInt(e.target.value) / 100})}
                  />
                </div>
              </div>
            </div>

            {/* Auto-Escalation */}
            <div className="space-y-3">
              <h4 className="font-medium text-sm">Auto-Escalation Timeouts (minutes)</h4>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Critical</Label>
                  <Input 
                    type="number"
                    min="1"
                    value={ruleForm.auto_escalate_critical_minutes}
                    onChange={(e) => setRuleForm({...ruleForm, auto_escalate_critical_minutes: parseInt(e.target.value)})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>High</Label>
                  <Input 
                    type="number"
                    min="1"
                    value={ruleForm.auto_escalate_high_minutes}
                    onChange={(e) => setRuleForm({...ruleForm, auto_escalate_high_minutes: parseInt(e.target.value)})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Medium</Label>
                  <Input 
                    type="number"
                    min="1"
                    value={ruleForm.auto_escalate_medium_minutes}
                    onChange={(e) => setRuleForm({...ruleForm, auto_escalate_medium_minutes: parseInt(e.target.value)})}
                  />
                </div>
              </div>
            </div>

            {/* Notifications */}
            <div className="space-y-3">
              <h4 className="font-medium text-sm">Send Notifications For</h4>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={ruleForm.notify_on_critical}
                    onChange={(e) => setRuleForm({...ruleForm, notify_on_critical: e.target.checked})}
                    className="w-4 h-4 rounded"
                  />
                  <span className="text-red-600">Critical</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={ruleForm.notify_on_high}
                    onChange={(e) => setRuleForm({...ruleForm, notify_on_high: e.target.checked})}
                    className="w-4 h-4 rounded"
                  />
                  <span className="text-orange-600">High</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={ruleForm.notify_on_medium}
                    onChange={(e) => setRuleForm({...ruleForm, notify_on_medium: e.target.checked})}
                    className="w-4 h-4 rounded"
                  />
                  <span className="text-yellow-600">Medium</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={ruleForm.notify_on_low}
                    onChange={(e) => setRuleForm({...ruleForm, notify_on_low: e.target.checked})}
                    className="w-4 h-4 rounded"
                  />
                  <span>Low</span>
                </label>
              </div>
            </div>

            {/* Active Toggle */}
            <div className="flex items-center gap-3">
              <input 
                type="checkbox" 
                id="is_active"
                checked={ruleForm.is_active}
                onChange={(e) => setRuleForm({...ruleForm, is_active: e.target.checked})}
                className="w-4 h-4 rounded"
              />
              <Label htmlFor="is_active">Rule is active</Label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsCreateRuleOpen(false)}>
                Cancel
              </Button>
              <Button type="submit">Create Rule</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Alert Rule Dialog */}
      <Dialog open={isEditRuleOpen} onOpenChange={setIsEditRuleOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Alert Rule</DialogTitle>
            <DialogDescription>
              Update alert rule settings
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditRule} className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_rule_name">Rule Name *</Label>
                <Input 
                  id="edit_rule_name" 
                  value={ruleForm.rule_name}
                  onChange={(e) => setRuleForm({...ruleForm, rule_name: e.target.value})}
                  required 
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_rule_type">Rule Type</Label>
                <Select value={ruleForm.rule_type} onValueChange={(v) => setRuleForm({...ruleForm, rule_type: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="threshold">Threshold Based</SelectItem>
                    <SelectItem value="pattern">Pattern Detection</SelectItem>
                    <SelectItem value="escalation">Escalation Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit_rule_description">Description</Label>
              <Input 
                id="edit_rule_description" 
                value={ruleForm.description}
                onChange={(e) => setRuleForm({...ruleForm, description: e.target.value})}
              />
            </div>

            {/* Thresholds */}
            <div className="space-y-3">
              <h4 className="font-medium text-sm">Confidence Thresholds</h4>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-red-600">Critical (%)</Label>
                  <Input 
                    type="number"
                    min="0" max="100" step="1"
                    value={Math.round(ruleForm.critical_threshold * 100)}
                    onChange={(e) => setRuleForm({...ruleForm, critical_threshold: parseInt(e.target.value) / 100})}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-orange-600">High (%)</Label>
                  <Input 
                    type="number"
                    min="0" max="100" step="1"
                    value={Math.round(ruleForm.high_threshold * 100)}
                    onChange={(e) => setRuleForm({...ruleForm, high_threshold: parseInt(e.target.value) / 100})}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-yellow-600">Medium (%)</Label>
                  <Input 
                    type="number"
                    min="0" max="100" step="1"
                    value={Math.round(ruleForm.medium_threshold * 100)}
                    onChange={(e) => setRuleForm({...ruleForm, medium_threshold: parseInt(e.target.value) / 100})}
                  />
                </div>
              </div>
            </div>

            {/* Auto-Escalation */}
            <div className="space-y-3">
              <h4 className="font-medium text-sm">Auto-Escalation Timeouts (minutes)</h4>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Critical</Label>
                  <Input 
                    type="number"
                    min="1"
                    value={ruleForm.auto_escalate_critical_minutes}
                    onChange={(e) => setRuleForm({...ruleForm, auto_escalate_critical_minutes: parseInt(e.target.value)})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>High</Label>
                  <Input 
                    type="number"
                    min="1"
                    value={ruleForm.auto_escalate_high_minutes}
                    onChange={(e) => setRuleForm({...ruleForm, auto_escalate_high_minutes: parseInt(e.target.value)})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Medium</Label>
                  <Input 
                    type="number"
                    min="1"
                    value={ruleForm.auto_escalate_medium_minutes}
                    onChange={(e) => setRuleForm({...ruleForm, auto_escalate_medium_minutes: parseInt(e.target.value)})}
                  />
                </div>
              </div>
            </div>

            {/* Notifications */}
            <div className="space-y-3">
              <h4 className="font-medium text-sm">Send Notifications For</h4>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={ruleForm.notify_on_critical}
                    onChange={(e) => setRuleForm({...ruleForm, notify_on_critical: e.target.checked})}
                    className="w-4 h-4 rounded"
                  />
                  <span className="text-red-600">Critical</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={ruleForm.notify_on_high}
                    onChange={(e) => setRuleForm({...ruleForm, notify_on_high: e.target.checked})}
                    className="w-4 h-4 rounded"
                  />
                  <span className="text-orange-600">High</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={ruleForm.notify_on_medium}
                    onChange={(e) => setRuleForm({...ruleForm, notify_on_medium: e.target.checked})}
                    className="w-4 h-4 rounded"
                  />
                  <span className="text-yellow-600">Medium</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={ruleForm.notify_on_low}
                    onChange={(e) => setRuleForm({...ruleForm, notify_on_low: e.target.checked})}
                    className="w-4 h-4 rounded"
                  />
                  <span>Low</span>
                </label>
              </div>
            </div>

            {/* Active Toggle */}
            <div className="flex items-center gap-3">
              <input 
                type="checkbox" 
                id="edit_is_active"
                checked={ruleForm.is_active}
                onChange={(e) => setRuleForm({...ruleForm, is_active: e.target.checked})}
                className="w-4 h-4 rounded"
              />
              <Label htmlFor="edit_is_active">Rule is active</Label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsEditRuleOpen(false)}>
                Cancel
              </Button>
              <Button type="submit">Save Changes</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Alert Rule Confirmation */}
      <AlertDialog open={isDeleteRuleOpen} onOpenChange={setIsDeleteRuleOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Alert Rule</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedRule?.rule_name}"? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteRule} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminConsolePage;
